import { Component, forwardRef, Input } from '@angular/core';
import { FieldWorkerLink } from 'src/shared/api';
import {
	FormControl,
	ControlValueAccessor,
	NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
	takeUntil,
	map,
	filter,
	distinctUntilChanged,
	skipWhile,
	first,
} from 'rxjs/operators';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import {
	displayName,
	idComparer,
	touchControl,
} from 'src/shared/utilities/utility-functions';
import { FormConfig } from '../form-cva.component';
import { LookUpsService } from 'src/shared/lookups.service';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

export const fieldManagerLinkFormConfig: FormConfig<FieldWorkerLink> = {
	id: [''],
	name: [''],
	firstName: [''],
	lastName: [''],
	employeeCode: [''],
	paycomId: [''],
	jobTitle: [''],
	payrollType: [''],
};

@Component({
	selector: 'rpshared-field-manager-link',
	templateUrl: './field-manager-link.component.html',
	styleUrls: ['./field-manager-link.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FieldManagerLinkComponent),
			multi: true,
		},
	],
})
export class FieldManagerLinkComponent
	extends SelfDestruct
	implements ControlValueAccessor
{
	private _required = false;
	@Input()
	get required(): boolean {
		return this._required;
	}
	set required(value: boolean) {
		this._required = coerceBooleanProperty(value);
	}
	fieldManager = new FormControl('') as FormControl;
	initialValue: FieldWorkerLink;

	readonly fieldManagers$ = this.lookUpsService.fieldManagers;
	readonly displayName = displayName;
	readonly idComparer = idComparer;

	constructor(private readonly lookUpsService: LookUpsService) {
		super();
	}

	// model --> view
	writeValue(value: FieldWorkerLink | number): void {
		if (typeof value === 'number') {
			this.lookUpsService.fieldManagers
				.pipe(
					first(),
					map((fieldManagers) =>
						fieldManagers.find((fw) => fw.id === value)
					)
				)
				.subscribe((fieldManager) => {
					this.initialValue = fieldManager;
					this.fieldManager.setValue(fieldManager, {
						emitEvent: false,
					});
				});
		} else if (value) {
			this.initialValue = value;
			this.fieldManager.setValue(value, { emitEvent: false });
		} else {
			this.fieldManager.reset('', { emitEvent: false });
		}
	}

	// view --> model
	registerOnChange(fn: (value: FieldWorkerLink | number) => void) {
		this.fieldManager.valueChanges
			.pipe(
				filter((v) => !v || typeof v !== 'string'),
				distinctUntilChanged(),
				takeUntil(this.destroyed)
			)
			.subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = touchControl(this.fieldManager)(fn);
	}

	onTouched: () => void = () => {};
}

@Component({
	selector: 'rpshared-field-manager-id',
	templateUrl: './field-manager-link.component.html',
	styleUrls: ['./field-manager-link.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FieldManagerIdComponent),
			multi: true,
		},
	],
})
export class FieldManagerIdComponent extends FieldManagerLinkComponent {
	registerOnChange(fn: (value: FieldWorkerLink | number) => void) {
		this.fieldManager.valueChanges
			.pipe(
				filter((v) => !v || typeof v !== 'string'),
				map((v) => (v ? v.id : null)),
				distinctUntilChanged(),
				skipWhile<number | null>((v) => !v && !this.initialValue),
				takeUntil(this.destroyed)
			)
			.subscribe(fn);
	}
}
